/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Divider,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UpdateCompanyProfileInput } from '@optimus/models';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectCompanyRoleOptions,
  selectGetCompanyRoleOptionsStatus,
  selectGetMemberInformationStatus,
  selectMemberInformation,
  selectMemberSession,
} from '../store/slices/onboardingSlice';
import {
  getCompanyRoles, getMemberInformation, updateMemberInformation,
} from '../store/thunks/onboardingThunk';
import AccountInformationSkeleton from './AccountInformationSkeleton';
import OptimusButton from './library/Button/Button';
import { validateField } from './utils/formUtils';
import { TestIds } from '../assets/testIds';
import { useCustomEventTracking } from './useCustomEventTracking';
import { IFormInputs } from './StyledFormInputs';
import { Member } from './models/Member';
import { AccountSettingsMemberFormControl } from '../components/Dashboard/Account/AccountSettingsMemberFormControl';
import { FormMethods, updatedCompanyRequest } from '../components/Dashboard/Partner/Enrollment/PartnerEnrollmentCardCommon';
import { updateCompanyProfile } from '../api/dashboardAPI';
import { AccountSettingsCompanyFormControl } from '../components/Dashboard/Account/AccountSettingsCompanyFormControl';

const SaveCTA = ({ loading, testId, disable = false }: {
  loading: boolean,
  testId: string
  disable?: boolean
}) => (
  <OptimusButton
    buttonText={loading ?
      <CircularProgress color="inherit" size={24} /> : 'Save'}
    customVariant="primary"
    size="m"
    fullWidth={false}
    disabled={disable}
    type='submit'
    data-testid={testId}
  />
);

const SubHeadingBox = ({ text }: { text: string }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minWidth: theme.spacing(25),
        maxWidth: theme.spacing(35),
        flex: '1 0 0',
      }}>
      <Typography variant='h6'>
        {text}
      </Typography>
    </Box>
  );
};

export default function AccountInformation() {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { memberId } = useAppSelector(selectMemberSession);
  const getCompanyRoleOptionsStatus = useAppSelector(selectGetCompanyRoleOptionsStatus);
  const getMemberInformationStatus = useAppSelector(selectGetMemberInformationStatus);
  const storeMemberInformation = useAppSelector(selectMemberInformation);
  const companyRoleOptions = useAppSelector(selectCompanyRoleOptions);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [loadingMemberInfo, setLoadingMemberInfo] = useState(false);
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);
  const { trackRequest, trackAccountSetting } = useCustomEventTracking();
  const isFetchingAccountInformation = useMemo(() => getCompanyRoleOptionsStatus === 'pending' ||
    getMemberInformationStatus === 'pending', [getMemberInformationStatus, getCompanyRoleOptionsStatus]);

  const getAccountInformationPageResources = () => {
    const getMemberInformationResponse = dispatch(getMemberInformation(
      {
        id: memberId || undefined,
        trackRequest,
      },
    ))
      .then(unwrapResult);

    const getCompanyRolesResponse = dispatch(getCompanyRoles(
      {
        trackRequest,
      },
    ))
      .then(unwrapResult);

    Promise.all([getMemberInformationResponse, getCompanyRolesResponse])
      .then(() => {
        setOpenErrorSnackbar(false);
      })
      .catch(() => {
        setOpenErrorSnackbar(true);
      });
  };

  useEffect(() => {
    getAccountInformationPageResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setOpenErrorSnackbar(false);
    setOpenSuccessSnackbar(false);
  };

  const accountMemberFormMethods = FormMethods(storeMemberInformation);
  const {
    getValues: getMemberValues,
    handleSubmit: handleMemberSubmit,
  } = accountMemberFormMethods;

  const accountCompanyFormMethods = FormMethods(storeMemberInformation);
  const {
    getValues: getCompanyValues,
    handleSubmit: handleCompanySubmit,
  } = accountCompanyFormMethods;

  const disableMemberUpdateCTA = useMemo(() => loadingMemberInfo, [loadingMemberInfo]);

  const disableCompanyUpdateCTA = useMemo(() => loadingCompanyInfo, [loadingCompanyInfo]);

  const checkMemberDataBeforeSend = (data: IFormInputs): void => {
    try {
      // Validate MemberInfo fields
      validateField(data.memberInfo.firstName, `${t('onboarding.questionPlaceholder.First Name')}`, t('error.required'));
      validateField(data.memberInfo.lastName, `${t('onboarding.questionPlaceholder.Last Name')}`, t('error.required'));
      // Phone Number is not required but if provided must be valid
      if (data.memberInfo.phone) {
        validateField(data.memberInfo.phone, `${t('onboarding.questionPlaceholder.phone')}`, t('error.invalid'));
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(`${t('error.generalMessage')}`);
      }
    }
  };

  const checkCompanyDataBeforeSend = (data: IFormInputs): void => {
    try {
      // Validate MemberInfo fields
      validateField(data.memberInfo.firstName, `${t('onboarding.questionPlaceholder.First Name')}`, t('error.required'));
      validateField(data.memberInfo.lastName, `${t('onboarding.questionPlaceholder.Last Name')}`, t('error.required'));

      // Validate BusinessInfo fields
      validateField(data.businessInfo.name, `${t('businessInformationForm.name')}`, t('error.required'));
      validateField(data.businessInfo.email, `${t('businessInformationForm.email')}`, t('error.required'));
      validateField(data.businessInfo.phone, `${t('businessInformationForm.phone')}`, t('error.required'));
      // Website is not required but if provided must be valid
      if (data.businessInfo.website) {
        validateField(data.businessInfo.website, `${t('businessInformationForm.website')}`, t('error.invalid'));
      }
      // Validate business address
      validateField(data.businessInfo.autoAddress?.fullAddress, `${t('businessInformationForm.address.heading')}`, t('error.required'));
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(`${t('error.generalMessage')}`);
      }
    }
  };

  const onSubmitMemberData = async (memberData: IFormInputs) => {
    if (memberData) {
      try {
        const selectedRoleId = getMemberValues('businessInfo.role');
        checkMemberDataBeforeSend(memberData);
        setLoadingMemberInfo(true);

        const payload: Member = {
          ...storeMemberInformation,
          firstName: getMemberValues('memberInfo.firstName'),
          lastName: getMemberValues('memberInfo.lastName'),
          email: getMemberValues('memberInfo.email'),
          phoneNumber: getMemberValues('memberInfo.phone'),
          companyRoleId: selectedRoleId,
          companyRole: {
            id: selectedRoleId || '',
            name: companyRoleOptions?.find(
              (option) => option.id === selectedRoleId,
            )?.name || '',
          },
        };

        dispatch(updateMemberInformation({ memberId, payload }))
          .then(unwrapResult)
          .then(() => {
            trackAccountSetting('Update', 'MemberInfo');
            setOpenSuccessSnackbar(true);
            setOpenErrorSnackbar(false);
            setLoadingMemberInfo(false);
            getAccountInformationPageResources();
          })
          .catch((error: unknown) => {
            setOpenSuccessSnackbar(false);
            setOpenErrorSnackbar(true);
            setLoadingMemberInfo(false);
          });
      } catch (error) {
        setOpenSuccessSnackbar(false);
        setOpenErrorSnackbar(true);
        setLoadingMemberInfo(false);
      }
    }
  };

  const onSubmitCompanyData = async (companyData: IFormInputs) => {
    if (companyData) {
      try {
        checkCompanyDataBeforeSend(companyData);
        setLoadingCompanyInfo(true);

        const updatedMemberInfoRequest = {
          firstName: getCompanyValues('memberInfo.firstName'),
          lastName: getCompanyValues('memberInfo.lastName'),
        };

        // add enrollment question only if user has answered it
        const enrollmentQuestions =
          getCompanyValues('companyConfig.partnerEnrollmentQuestions')?.questions || {};

        const hasAnyAnswer = Object.values(enrollmentQuestions).some(
          (q: any) => q?.answer && q.answer.trim() !== '',
        );
        const companyProfileUpdateRequest: UpdateCompanyProfileInput = {
          memberInfoUpdate: updatedMemberInfoRequest,
          companyInfoUpdate: {
            ...updatedCompanyRequest(getCompanyValues),
            ...(hasAnyAnswer && {
              partnerEnrollmentQuestions: getCompanyValues('companyConfig.partnerEnrollmentQuestions'),
            }),
          },
        };

        updateCompanyProfile(
          companyProfileUpdateRequest,
          storeMemberInformation?.company?.id as string,
        )
          .then(() => {
            dispatch(getMemberInformation({ trackRequest }));
            setOpenErrorSnackbar(false);
            setOpenSuccessSnackbar(true);
            setLoadingCompanyInfo(false);
            trackAccountSetting('Update', 'CompanyProfile');
          })
          .catch(() => {
            setOpenErrorSnackbar(true);
            setOpenSuccessSnackbar(false);
            setLoadingCompanyInfo(false);
          });
      } catch (error) {
        setOpenSuccessSnackbar(false);
        setOpenErrorSnackbar(true);
        setLoadingCompanyInfo(false);
      }
    }
  };

  return (
    <>
      <Snackbar
        sx={() => ({
          [theme.breakpoints.up('md')]: {
            width: '60%',
            maxWidth: theme.spacing(126.5),
          },
        })}
        open={openSuccessSnackbar}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="success" sx={{ width: '100%' }} onClose={handleClose}>
          <AlertTitle>{t('success.generalMessage2')}</AlertTitle>
          {t('success.account')}
        </Alert>
      </Snackbar>
      <Snackbar
        sx={() => ({
          [theme.breakpoints.up('md')]: {
            width: '60%',
            maxWidth: theme.spacing(126.5),
          },
        })}
        open={openErrorSnackbar}
        autoHideDuration={10000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }} onClose={handleClose}>
          <AlertTitle>{t('error.generalMessage2')}</AlertTitle>
          {t('error.account')}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(3),
          alignSelf: 'stretch',
        }}>
        {/* Member Info Component */}
        <Box
          sx={{
            display: 'flex',
            gap: `${theme.spacing(2)} ${theme.spacing(4)}`,
            alignSelf: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          <SubHeadingBox text={t('onboarding.account.heading')} />
          {isFetchingAccountInformation ?
            <AccountInformationSkeleton /> :
            <Box
              sx={{
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              }}
            >
              <FormProvider {...accountMemberFormMethods} >
                <form onSubmit={handleMemberSubmit(onSubmitMemberData)}>
                  <AccountSettingsMemberFormControl
                    memberInformation={storeMemberInformation}
                    companyRoleOptions={companyRoleOptions}
                  />
                  <SaveCTA
                    loading={loadingMemberInfo}
                    testId={TestIds.accountSettingsPage.saveMemberInfoBtn}
                    disable={disableMemberUpdateCTA}
                  />
                </form>
              </FormProvider>
            </Box>
          }
        </Box>
        <Divider
          sx={{
            height: theme.spacing(0.125),
            alignSelf: 'stretch',
          }}
        />
        {/* Company Info Component */}
        <Box
          sx={{
            display: 'flex',
            gap: `${theme.spacing(2)} ${theme.spacing(4)}`,
            alignSelf: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          <SubHeadingBox text={t('onboarding.company.heading')} />
          {isFetchingAccountInformation ?
            <AccountInformationSkeleton /> :
            <Box
              sx={{
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              }}
            >
              <FormProvider {...accountCompanyFormMethods} >
                <form onSubmit={handleCompanySubmit(onSubmitCompanyData)}>
                  <AccountSettingsCompanyFormControl />
                  <SaveCTA
                    loading={loadingCompanyInfo}
                    testId={TestIds.accountSettingsPage.saveCompanyInfoBtn}
                    disable={disableCompanyUpdateCTA}
                  />
                </form>
              </FormProvider>
            </Box>
          }
        </Box>
      </Box >
    </>
  );
}
